import React from 'react';
import styled from 'styled-components';
import { compose, withState } from 'recompose';
import { Polygon, Marker } from 'react-google-maps';
import debounce from 'lodash/debounce';
import delay from 'lodash/delay';
import attempt from 'lodash/attempt';
import isError from 'lodash/isError';

import { Map as MapComponent } from 'shared/components/presentational/Map';
import { MAP_OPTIONS } from 'shared/constants/map';
import mediaQueries from 'shared/helpers/styled-components/mediaQueries';
import getBoundariesFromGoogleMap from 'shared/helpers/map/getBoundariesFromGoogleMap';
import MapSearch from './MapSearch';
import MapHomeMarkers from './MapHomeMarkers';
import { ZOOM_LEVELS } from '../constants';

const Wrapper = styled.section`
  display: block;
  position: relative;
  padding-top: 20px;
  height: 400px;
`;

const MapOverlay = styled.div`
  position: absolute;
  z-index: 2;
  margin: 0 auto;
  width: 540px;
  top: 0;
  left: 0;
  right: 0;
  ${mediaQueries.toPhablet`
    width: 360px;
    position: initial;
  `};
  ${mediaQueries.toTablet`
    position: initial;
    margin-bottom: 5px;
  `};
`;

class Map extends React.PureComponent {
  state = {
    selectedHome: null,
    zoom: ZOOM_LEVELS.DEFAULT,
    center: null,
    search: null
  };

  getHomes = debounce(
    boundaries => {
      const { fetchHomes, handleHomesLoad } = this.props;
      fetchHomes(boundaries)
        .then(handleHomesLoad)
        .catch(() => {
          handleHomesLoad([]);
        });
    },
    1000,
    { leading: true }
  );

  getShapes = () => {
    const { fetchShapes, handleShapesLoad } = this.props;
    fetchShapes().then(handleShapesLoad);
  };

  componentDidMount() {
    this.getShapes();
  }

  handleMapRef = mapRef => {
    this.mapRef = mapRef;
  };

  handleZoomChange = (zoom, boundaries) => {
    this.getHomes(boundaries);
    this.setState({ zoom });
  };

  handleDragEnd = (center, boundaries) => {
    this.getHomes(boundaries);
  };

  handleHomeSelect = (home = null) => {
    this.setState({ selectedHome: home });
  };

  handleSearch = ({ position }) => {
    this.setState({
      center: position,
      zoom: ZOOM_LEVELS.FOCUSED,
      search: position
    });

    // wait for the map to be moved to get new boundaries
    delay(() => {
      const boundaries = attempt(() => getBoundariesFromGoogleMap(this.mapRef));

      if (!isError(boundaries)) {
        this.getHomes(boundaries);
      }
    }, 500);
  };

  render() {
    const { handleHomeSelect, handleSearch } = this;
    const { shapes, homes, countryCoordinates, t } = this.props;
    const { selectedHome, zoom, center, search } = this.state;
    return (
      <Wrapper>
        <MapOverlay>
          <MapSearch onSearch={handleSearch} />
        </MapOverlay>
        <MapComponent
          onRefResolve={this.handleMapRef}
          zoom={zoom}
          defaultCenter={countryCoordinates}
          center={center}
          onZoomChange={this.handleZoomChange}
          onDragEnd={this.handleDragEnd}
          options={MAP_OPTIONS}
        >
          <Polygon
            paths={shapes}
            options={{
              fillColor: '#FFD700',
              strokeOpacity: 0,
              fillOpacity: zoom === null || zoom < 16 ? 0.5 : 0
            }}
          />
          <MapHomeMarkers
            onMarkerClick={handleHomeSelect}
            onInfoWindowClose={handleHomeSelect}
            homes={homes}
            selectedHome={selectedHome}
            t={t}
          />
          {search && <Marker position={search} />}
        </MapComponent>
      </Wrapper>
    );
  }
}

export default compose(
  withState('shapes', 'handleShapesLoad', []),
  withState('homes', 'handleHomesLoad', [])
)(Map);
